<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <v-dialog
    v-model="dialog"
    persistent
    id="addUser"
    width="500px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title class="py-6">
        <div
          class="text-h6 text-sm-h4"
          style="word-break: break-word;"
        >{{ language.t('AddUser.addUser') }}</div>
      </v-card-title>
      <v-card-text>
        <v-alert
          v-model="error"
          type="error"
          dismissible
        >{{ errorMessage }}</v-alert>
        <v-text-field
          v-if="!emailChecked"
          v-model="checkEmailAddress"
          :label="'*' + language.t('Forms.emailAddress')"
          required
          :rules="emailRules"
        >

        </v-text-field>
        <v-form
          v-if="emailChecked && createNewUser"
          ref="form"
          v-model="valid"
          lazy-validation
          v-on:submit.prevent="createUser"
        >
          <v-text-field
            v-model="form.firstName"
            :label="language.t('Forms.firstName')"
          ></v-text-field>
          <v-text-field
            v-model="form.middleName"
            :label="language.t('Forms.middleName')"
          ></v-text-field>
          <v-text-field
            v-model="form.lastName"
            :label="'*' + language.t('Forms.lastName')"
            required
            :rules="nameRules"
          >
          </v-text-field>
          <v-text-field
            v-model="form.emailAddress"
            :label="'*' + language.t('Forms.emailAddress')"
            required
            :rules="emailRules"
            disabled
          >
          </v-text-field>
          <v-text-field
            v-model="form.mobilePhoneNumber"
            :label="language.t('Forms.phoneNumber')"
          >
          </v-text-field>
          <v-text-field
            v-if="!useRandomPassword"
            v-model="form.password"
            :label="'*' + language.t('Forms.password') + ' (' + language.t('Forms.requiredPasswordCharacters') + ')'"
            type="password"
            required
            :rules="passwordRules"
            :disabled="useRandomPassword"
          ></v-text-field>
          <v-checkbox
            v-model="useRandomPassword"
            :label="language.t('AddUser.generatePassword')"
            @change="validation"
          >
          </v-checkbox>
          <p class="text-right">* = {{ language.t('Forms.requiredFields') }}</p>
          <v-btn
            color="success"
            class="mr-4"
            type="submit"
            :disabled="!valid || !complete"
            :loading="saving"
            @click="checkValidButtonClick"
          >
            {{ language.t('AddUser.addUser') }}
          </v-btn>
        </v-form>
        <div v-if="emailChecked && !createNewUser">{{ language.t('AddUser.userExists') }}
        </div>
      </v-card-text>
      <v-card-actions class="card-actions">
        <v-btn
          v-if="emailChecked && !createNewUser"
          color="success"
          class="mr-4"
          @click="addAccount"
        >
          <v-icon>mdi-check</v-icon>
        </v-btn>
        <v-btn
          v-if="emailChecked && !createNewUser"
          color="error"
          class="mr-4"
          @click="reset"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-btn
          v-if="!emailChecked"
          color="success"
          class="mr-4"
          @click="checkEmail"
          :loading="loading"
        >
          {{ language.t('AddUser.nextStep') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="blue darken-1"
          text
          @click="$emit('close');"
        >
          <v-icon>mdi-close</v-icon>{{ language.t('App.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  components: {},
  props: {},
  data() {
    return {
      emailChecked: false,
      checkEmailAddress: "",
      createNewUser: false,
      accountId: null,
      loading: false,

      dialog: true,
      valid: true,
      checkbox: false,
      complete: false,
      saving: false,
      error: false,
      errorMessage: "",
      success: false,
      useRandomPassword: false,

      nameRules: [(v) => !!v || this.language.t("FormRules.lastnameRequired")],
      emailRules: [(v) => !!v || this.language.t("FormRules.emailRequired"), (v) => /.+@.+\..+/.test(v) || this.language.t("FormRules.emailValid")],
      passwordRules: [(v) => !!v || this.language.t("FormRules.passwordRequired"), (v) => v.length >= 8 || this.language.t("FormRules.passwordLength")],
      form: {
        firstName: "",
        middleName: "",
        lastName: "",
        emailAddress: "",
        mobilePhoneNumber: "",
        password: "",
      },
    };
  },
  mounted: function () { },
  computed: {},
  watch: {
    form: {
      /* validate if the form data is complete */
      handler() {
        this.validation();
      },
      deep: true,
    },
  },
  methods: {
    reset() {
      this.emailChecked = false;
      this.checkEmailAddress = "";
      this.createNewUser = false;
      this.accountId = null;
      console.log("TODO: reset email check");
    },
    addAccount() {
      this.saving = true;
      let data = new FormData();
      data.set("accountId", this.accountId);
      this.$axios
        .post(route("api.user.customer.user.add"), data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          console.log(res);
          this.success = true;
          this.saving = false;
          this.$emit("update");
          this.$emit("close");
        });
    },
    checkEmail() {
      this.loading = true;
      this.$axios.get(route("api.user.email", this.checkEmailAddress)).then((res) => {
        console.log(res);
        this.loading = false;
        this.emailChecked = true;
        if (!res.data.account) {
          this.error = false;
          this.createNewUser = true;
          this.form.emailAddress = this.checkEmailAddress;
          console.log("create new user");
        } else if (!res.data.customers) {
          this.error = false;
          console.log("add user to customer");
          this.accountId = res.data.account;
          // add to customer
        } else {
          // maybe add this user also to company?
          console.log("email already used");
          this.error = true;
          this.errorMessage = this.language.t("AddUser.emailUsed");
          // already has customer
          this.emailChecked = false;
        }
      });
    },
    checkValidButtonClick() {
      this.$refs.form.validate();
    },
    validation() {
      if (this.form.lastName != "" && this.form.emailAddress != "") {
        if (this.form.password != "" || this.useRandomPassword) {
          this.complete = true;
        } else {
          this.complete = false;
        }
      } else this.complete = false;
    },
    createUser() {
      if (this.valid) {
        var _this = this;
        _this.saving = true;
        _this.error = null;
        let data = new FormData();
        data.set("firstName", this.form.firstName);
        data.set("middleName", this.form.middleName);
        data.set("lastName", this.form.lastName);
        data.set("emailAddress", this.form.emailAddress);
        data.set("mobilePhoneNumber", this.form.mobilePhoneNumber);
        data.set("password", this.form.password);
        this.useRandomPassword && data.set("useRandomPassword", true);
        console.log(this.form);
        console.log(data.get("useRandomPassword"));

        this.$axios
          .post(route("api.user.customer.user.create"), data, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((res) => {
            console.log(res);
            if (res.data.success) {
              _this.success = true;
              _this.saving = false;
              _this.$emit("update");
              _this.$emit("close");
            } else {
              console.log(res.data.message);
              _this.error = true;
              _this.errorMessage = res.data.message;
              _this.success = false;
              _this.saving = false;
            }
          })
          .catch((error) => {
            _this.saving = false;
            console.log(error.response.data);
            if (error.response.data.error) {
              _this.error = true;
              _this.errorMessage = error.response.data.error;
            } else {
              _this.error = true;
              _this.errorMessage = "unknown server error";
            }
          });
      }
    },
  },
};
</script>
<style></style>